import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { systemConfig } from '@/config/systemConfig';
import InspectionStatus from '@/enums/InspectionStatus.enum';
import { mergeSizeCharts, upsertInspection } from '@/store/slices/inspectionsSlice';
import { AppDispatch } from '@/store/store';
import { Inspection, InspectionDetailApiResponse } from '@/types/Inspection.type';
import { SizeChart } from '@/types/SizeChart';
import { clivApiRequest } from '@/utils/apiClient';

export type InspectionHydrateFailureReason = 'not_found' | 'offline_lite' | 'fetch_failed';

export type InspectionHydrateResult =
    | { ok: true; inspection: Inspection }
    | { ok: false; reason: InspectionHydrateFailureReason };

function splitInspectionDetailResponse(data: InspectionDetailApiResponse): {
    inspection: Inspection;
    sizeCharts: SizeChart[];
} {
    const { sizeCharts: charts = [], ...rest } = data;
    return { inspection: rest as Inspection, sizeCharts: charts };
}

type PulledInspectionDetail =
    | { ok: true; inspection: Inspection; sizeCharts: SizeChart[] }
    | { ok: false; reason: 'fetch_failed' };

async function pullInspectionDetailFromApi(inspectionId: string): Promise<PulledInspectionDetail> {
    const url = `${systemConfig.apiUrl}/${API_ENDPOINTS.INSPECTION.INSPECTIONS}/${inspectionId}`;
    const res = await clivApiRequest<InspectionDetailApiResponse>({
        url,
        params: { context: 'inspectApp' },
    });
    if (!res.ok || !res.data) {
        return { ok: false, reason: 'fetch_failed' };
    }
    const { inspection, sizeCharts } = splitInspectionDetailResponse(res.data);
    return { ok: true, inspection, sizeCharts };
}

function mergeLocalDraftFields(
    apiInspection: Inspection,
    localInspection: Inspection | null | undefined,
): Inspection {
    if (!localInspection) {
        return apiInspection;
    }

    const merged: Inspection = { ...apiInspection };

    if (
        (!merged.verificationImages || merged.verificationImages.length === 0) &&
        localInspection.verificationImages &&
        localInspection.verificationImages.length > 0
    ) {
        merged.verificationImages = structuredClone(localInspection.verificationImages);
    }

    if (!merged.verificationImageUrls && localInspection.verificationImageUrls) {
        merged.verificationImageUrls = localInspection.verificationImageUrls;
    }

    if (typeof localInspection.isLocationVerified === 'boolean') {
        merged.isLocationVerified = localInspection.isLocationVerified;
    }

    return merged;
}

/**
 * Fetches latest inspection + size charts from the API (inspect-app context).
 * Does not check online status; callers must only invoke when online.
 */
export async function fetchLatestInspectionFromApi(
    inspectionId: string,
    dispatch: AppDispatch,
): Promise<InspectionHydrateResult> {
    const pulled = await pullInspectionDetailFromApi(inspectionId);
    if (!pulled.ok) {
        return pulled;
    }
    dispatch(upsertInspection(pulled.inspection));
    if (pulled.sizeCharts.length > 0) {
        dispatch(mergeSizeCharts(pulled.sizeCharts));
    }
    return { ok: true, inspection: pulled.inspection };
}

/**
 * Offline: saved copy wins; otherwise only a fully preloaded list row may be used.
 * Lite rows require a connection.
 */
export function resolveOfflineInspectionSource(
    savedInspection: Inspection | null | undefined,
    listRow: Inspection | null | undefined,
): InspectionHydrateResult {
    if (savedInspection) {
        return { ok: true, inspection: structuredClone(savedInspection) };
    }
    if (listRow?.isFullDetailPreloaded === true && !inspectionNeedsApiHydration(listRow)) {
        return { ok: true, inspection: structuredClone(listRow) };
    }
    if (listRow) {
        return { ok: false, reason: 'offline_lite' };
    }
    return { ok: false, reason: 'not_found' };
}

export type LoadInspectionForInspectAppParams = {
    inspectionId: string;
    isOnline: boolean;
    savedInspection: Inspection | null | undefined;
    listRow: Inspection | null | undefined;
    dispatch: AppDispatch;
};

/**
 * Online: always refresh from GET /inspections/:id (latest server data).
 * Offline: saved inspection or fully preloaded list row only; lite rows fail with offline_lite.
 */
export async function loadInspectionForInspectApp(
    params: LoadInspectionForInspectAppParams,
): Promise<InspectionHydrateResult> {
    const { inspectionId, isOnline, savedInspection, listRow, dispatch } = params;
    if (isOnline) {
        const pulled = await pullInspectionDetailFromApi(inspectionId);
        if (!pulled.ok) {
            return pulled;
        }
        const localSource = savedInspection ?? listRow;
        const inspection = mergeLocalDraftFields(pulled.inspection, localSource);
        dispatch(upsertInspection(inspection));
        if (pulled.sizeCharts.length > 0) {
            dispatch(mergeSizeCharts(pulled.sizeCharts));
        }
        return { ok: true, inspection };
    }
    return resolveOfflineInspectionSource(savedInspection, listRow);
}

/**
 * @deprecated Prefer fetchLatestInspectionFromApi or loadInspectionForInspectApp.
 */
export async function hydrateInspectionFromApi(
    inspectionId: string,
    dispatch: AppDispatch,
): Promise<InspectionHydrateResult> {
    return fetchLatestInspectionFromApi(inspectionId, dispatch);
}

function isClosedInspectionStatus(inspectionStatusId?: string): boolean {
    return inspectionStatusId?.toLowerCase() === InspectionStatus.CLOSED.toLowerCase();
}

/**
 * Returns whether the list row omitted heavy payload (needs online refresh before use).
 * Closed inspections always need a fresh fetch for inspect-app use (not treated as offline-capable).
 */
export function inspectionNeedsApiHydration(
    inspection: Pick<Inspection, 'isFullDetailPreloaded' | 'inspectionStatusId'>,
): boolean {
    if (isClosedInspectionStatus(inspection.inspectionStatusId)) {
        return true;
    }
    return inspection.isFullDetailPreloaded === false;
}
